import { UserOutlined } from '@ant-design/icons'
import { Button, Steps, Tag, Tooltip, Typography, message } from 'antd'
import { inject, observer } from 'mobx-react'
import React, { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import { ASSIGNEE_TYPE, PROPOSAL_STATUS } from '../../../../constants'
import { blue, redPrimary } from '../../../../color'
import SelectPeoplePopup from '../../../../components/SelectPeoplePopup'
import date_format from '../../../../date_format'

const { Step } = Steps

const getInfoUser = (username, departmentWithUserList) => {
  const user = {
    full_name: username,
    image_id: null,
    username: username,
  }
  departmentWithUserList.forEach(department => {
    const userTarget = department.users.find(
      user => user.user_name === username
    )
    if (userTarget) {
      user.full_name = userTarget.name_uppercase || username
      user.image_id = userTarget.image_id
    }
  })
  return user
}

const renderStatus = (status, isRejected) => {
  const styleTag = {
    margin: 0,
    borderRadius: 4,
    border: 'none',
    padding: '2px 6px',
  }

  if (status === PROPOSAL_STATUS.PENDING) {
    return (
      <Tag
        color={isRejected ? null : 'geekblue'}
        style={{
          ...styleTag,
          opacity: isRejected ? 0.5 : 1,
        }}>
        Đến lượt duyệt
      </Tag>
    )
  }
  if (status === PROPOSAL_STATUS.WAITING) {
    return (
      <Tag
        color={isRejected ? null : 'orange'}
        style={{
          ...styleTag,
          opacity: isRejected ? 0.5 : 1,
        }}>
        Chờ duyệt
      </Tag>
    )
  }

  if (status === PROPOSAL_STATUS.CREATED) {
    return (
      <Tag
        style={{
          ...styleTag,
          backgroundColor: '#E6F5F0',
          color: '#2CA87F',
        }}>
        Đã tạo
      </Tag>
    )
  }
  if (status === PROPOSAL_STATUS.APPROVED) {
    return (
      <Tag color={'green'} style={styleTag}>
        Đã duyệt
      </Tag>
    )
  }
  if (status === PROPOSAL_STATUS.REJECTED) {
    return (
      <Tag color={'red'} style={styleTag}>
        Đã từ chối
      </Tag>
    )
  }
}

const ASSIGNEE_TYPE_CONFIG = [
  ASSIGNEE_TYPE.CREATE,
  ASSIGNEE_TYPE.COMBINER,
  ASSIGNEE_TYPE.HANDLER,
  ASSIGNEE_TYPE.LEADER,
  ASSIGNEE_TYPE.UNIT_LEADER,
]

const ProposalStepApproveV2 = props => {
  const {
    proposalStore,
    selectPeopleStore,
    approvalLevelList,
    isCheckedFirstStep,
    isEditMode = true,
  } = props

  const { originDepartmentWithUserList, selectUserData } = selectPeopleStore
  const [currentStep, setCurrentStep] = useState(0)
  const [approvalLevels, setApprovalLevels] = useState([])
  const [isVisibleSelectUserPopup, setIsVisibleSelectUserPopup] = useState(
    false
  )
  const [statusStep, setStatusStep] = useState('process')

  useEffect(() => {
    const length = approvalLevelList.length
    if (approvalLevelList.length === 0) return
    let step = 0
    const pendingStep = approvalLevelList.find(
      item => item.status === PROPOSAL_STATUS.PENDING
    )
    const endStep = approvalLevelList[length - 1]
    const stepRejected = approvalLevelList.find(
      item => item.status === PROPOSAL_STATUS.REJECTED
    )
    if (pendingStep) {
      step = pendingStep.level
    }
    if (endStep.status === PROPOSAL_STATUS.APPROVED) {
      step = length
    }
    if (stepRejected) {
      step = stepRejected?.level ?? stepRejected?.levelselectUserData ?? 0
      setStatusStep('error')
    } else {
      setStatusStep('')
    }
    setCurrentStep(step)
  }, [approvalLevelList])

  useEffect(() => {
    const approvalLevelsUpdate = approvalLevels.map(item => {
      if (!item.allow_edit) {
        return item
      }
      const assignTypeIndex = approvalLevels
        .filter(el => el.allow_edit)
        .findIndex(el => el.level === item.level)
      const getUser = index => {
        if (![0, 1, 2, 3, 4].includes(index)) {
          return {}
        }
        return selectUserData[ASSIGNEE_TYPE_CONFIG[index]]?.length > 0
          ? selectUserData[ASSIGNEE_TYPE_CONFIG[index]][0]
          : {}
      }
      return {
        ...item,
        username: getUser(assignTypeIndex).id,
        user: {
          full_name: getUser(assignTypeIndex).full_name,
          image_id: getUser(assignTypeIndex).image_id,
          username: getUser(assignTypeIndex).id,
        },
      }
    })
    setApprovalLevels(approvalLevelsUpdate)
  }, [selectUserData])

  useEffect(() => {
    proposalStore.setDefaultApprovalConfigUpdate(approvalLevels)
  }, [approvalLevels])

  useEffect(() => {
    const approvalLevelConvert = approvalLevelList.map(item => {
      return {
        ...item,
        user: getInfoUser(item.username, originDepartmentWithUserList),
      }
    })
    const levelAllowEditList = approvalLevelConvert.filter(
      item => item.allow_edit
    )
    let create = null
    let combiner = null
    let handler = null
    let leader = null
    let unitLeader = null
    levelAllowEditList.forEach((item, index) => {
      if (!item.username) return
      const user = getInfoUser(item.username, originDepartmentWithUserList)
      if (index === 0) {
        create = user
      }
      if (index === 1) {
        combiner = user
      }
      if (index === 2) {
        handler = user
      }
      if (index === 3) {
        leader = user
      }
      if (index === 4) {
        unitLeader = user
      }
    })
    selectPeopleStore.setSelectUserData({
      ...selectUserData,
      [ASSIGNEE_TYPE.CREATE]: create
        ? [
            {
              assignee_type: ASSIGNEE_TYPE.USER,
              full_name: create?.full_name,
              id: create?.username,
              image_id: create?.image_id,
              permission: ASSIGNEE_TYPE.CREATE,
            },
          ]
        : [],
      [ASSIGNEE_TYPE.COMBINER]: combiner
        ? [
            {
              assignee_type: ASSIGNEE_TYPE.USER,
              full_name: combiner?.full_name,
              id: combiner?.username,
              image_id: combiner?.image_id,
              permission: ASSIGNEE_TYPE.COMBINER,
            },
          ]
        : [],
      [ASSIGNEE_TYPE.HANDLER]: handler
        ? [
            {
              assignee_type: ASSIGNEE_TYPE.USER,
              full_name: handler?.full_name,
              id: handler?.username,
              image_id: handler?.image_id,
              permission: ASSIGNEE_TYPE.HANDLER,
            },
          ]
        : [],
      [ASSIGNEE_TYPE.LEADER]: leader
        ? [
            {
              assignee_type: ASSIGNEE_TYPE.USER,
              full_name: leader?.full_name,
              id: leader?.username,
              image_id: leader?.image_id,
              permission: ASSIGNEE_TYPE.LEADER,
            },
          ]
        : [],
      [ASSIGNEE_TYPE.UNIT_LEADER]: unitLeader
        ? [
            {
              assignee_type: ASSIGNEE_TYPE.USER,
              full_name: unitLeader?.full_name,
              id: unitLeader?.username,
              image_id: unitLeader?.image_id,
              permission: ASSIGNEE_TYPE.UNIT_LEADER,
            },
          ]
        : [],
    })
    setApprovalLevels(approvalLevelConvert)
  }, [approvalLevelList, originDepartmentWithUserList])

  const handleSubmitSelectUser = useCallback(() => {
    setIsVisibleSelectUserPopup(false)
  }, [])

  const handleOpenSelectUser = level => {
    let userType = null
    const levelAllowEditList = approvalLevelList.filter(item => item.allow_edit)
    const indexLevelAllowEdit = levelAllowEditList.findIndex(
      item => item.level === level
    )
    if (indexLevelAllowEdit !== -1) {
      userType = ASSIGNEE_TYPE_CONFIG[indexLevelAllowEdit]
    }
    if (userType) {
      selectPeopleStore.setAssigneePopupType(userType)
      setIsVisibleSelectUserPopup(true)
    } else {
      return message.warning('Không rõ chọn người loại nào!')
    }
  }

  const renderDescStep = level => {
    return (
      <>
        <div
          style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
          {level?.user?.username ? (
            <div>
              <p
                style={{ marginBottom: 0, fontSize: '12px', color: '#7C8694' }}>
                {level.name}
              </p>
              <div
                style={{
                  display: 'flex',
                  gap: '10px 4px',
                  alignItems: 'flex-end',
                  flexWrap: 'wrap',
                  justifyContent: 'flex-start',
                }}>
                <span
                  style={{
                    marginBottom: 0,
                    fontSize: '14px',
                    color: '#000000',
                    fontWeight: 600,
                    textTransform: 'capitalize',
                  }}>
                  {level.user?.full_name?.toLowerCase()}
                </span>
                {Boolean(level?.allow_edit && isEditMode) && (
                  <span
                    style={{
                      cursor: 'pointer',
                      fontSize: '12px',
                      color: blue,
                    }}
                    onClick={() => handleOpenSelectUser(level.level)}>
                    Chỉnh sửa
                  </span>
                )}
              </div>
            </div>
          ) : (
            <div>
              {Boolean(level?.allow_edit && isEditMode) && (
                <span
                  style={{
                    cursor: 'pointer',
                    fontSize: '12px',
                    color: blue,
                  }}
                  onClick={() => handleOpenSelectUser(level.level)}>
                  Chọn người duyệt
                </span>
              )}
            </div>
          )}

          <div
            style={{
              flexShrink: 0,
            }}>
            {renderStatus(level.status, statusStep === 'error')}
          </div>
        </div>
        {level.comment && level.comment !== '' && (
          <div style={{ marginTop: 4, fontStyle: 'italic' }}>
            <Typography.Paragraph
              ellipsis={{
                rows: 2,
                expandable: true,
                onEllipsis: ellipsis => {
                  console.log('Ellipsis changed:', ellipsis)
                },
              }}
              title={level.comment}
              style={{ fontSize: 12, marginBottom: 0 }}>
              <span style={{ fontWeight: 600, marginRight: 6 }}>Lý do:</span>
              {level.comment}
            </Typography.Paragraph>
          </div>
        )}
        <div style={{ height: 24 }} />
      </>
    )
  }
  return (
    <div>
      <h3 style={{ marginBottom: 16, fontSize: 18, fontWeight: 600 }}>
        Tiến trình duyệt
      </h3>

      <Steps
        direction="vertical"
        status={statusStep}
        className="custom-proposal-steps"
        labelPlacement={'vertical'}
        current={isCheckedFirstStep ? 1 : currentStep}
        size={'small'}>
        {approvalLevels.map((level, index) => {
          return (
            <Step
              key={level.level}
              title={null}
              description={renderDescStep(level)}
            />
          )
        })}
      </Steps>
      <SelectPeoplePopup
        onlyOneUser
        handleCancelSelectUser={handleSubmitSelectUser}
        handleSubmitSelectUser={handleSubmitSelectUser}
        isVisibleSelectPeoplePopup={isVisibleSelectUserPopup}
        isUserDisableList={false}
      />
    </div>
  )
}

export default inject(
  'authenticationStore',
  'proposalStore',
  'selectPeopleStore'
)(observer(ProposalStepApproveV2))
